/**
 * Comprovantes do lote — gera o comprovante (story) de CADA solicitação de
 * origem do lote MDF e permite: baixar os JPGs, baixar um PDF único e
 * imprimir, com escolha de posicionamento (1, 2 ou 4 por folha A4) para
 * aproveitar o papel.
 *
 * Fluxo: detalhe do lote → `solicitacoes` distintas → detalhe de cada uma →
 * cards renderizados fora da tela (comprovanteRender) → JPGs em série.
 *
 * As IMAGENS anexadas às solicitações entram na mesma lista de seleção, e a
 * paginação (`montarPaginasAgrupadas`) mantém cada foto na mesma folha do
 * comprovante dela. Toda imagem impressa leva cabeçalho (item · ASM · lote) —
 * em HTML na impressão e em texto vetorial no PDF, nunca desenhado dentro da
 * foto, que é comprimida a 1280px e borraria o texto.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiDownload, FiFileText, FiPrinter } from "react-icons/fi";
import { jsPDF } from "jspdf";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { obterAssistencia, obterLote } from "../../../services/solicitacoes";
import {
  ComprovanteCardView, baixarBlob, jpgDeElemento,
} from "../Solicitacoes/comprovanteRender";
import { paginasDaSolicitacao } from "../Solicitacoes/imprimirImagens";
import { montarPaginasAgrupadas } from "./paginacaoAgrupada";
import { CompOffscreen } from "../Solicitacoes/styles";
import { dataUrlParaBlob } from "../../../utils/imagemCanvas";
import {
  Checkbox,
  CompLoteGrid,
  CompLoteThumb,
  EmptyMsg,
  ErrorMsg,
  GhostBtn,
  Label,
  ListaHeadRow,
  RowEnd,
  Select,
} from "./styles";
import { Carregando, Vazio } from "../_shared/Estados";

// Folha A4 retrato, margem 8mm, espaço entre células 4mm (medidas em mm).
const PAGINA = { largura: 210, altura: 297, margem: 8, gap: 4 };

const LAYOUTS = [
  { value: 1, label: "1 por folha", cols: 1, rows: 1 },
  { value: 2, label: "2 por folha (lado a lado)", cols: 2, rows: 1 },
  { value: 4, label: "4 por folha (2 × 2)", cols: 2, rows: 2 },
];

function geometria(layout) {
  const utilW = PAGINA.largura - 2 * PAGINA.margem;
  const utilH = PAGINA.altura - 2 * PAGINA.margem;
  const celW = (utilW - PAGINA.gap * (layout.cols - 1)) / layout.cols;
  const celH = (utilH - PAGINA.gap * (layout.rows - 1)) / layout.rows;
  return { utilW, utilH, celW, celH };
}

/**
 * Escapa texto que entra na janela de impressão.
 *
 * Vale também para o `src` das imagens: são data URLs vindas do banco e uma
 * aspa solta ali fecharia o atributo e injetaria HTML na janela nova.
 */
function esc(v) {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Prepara uma foto para o jsPDF: mede (o fit contain precisa da proporção) e
 * garante um formato que a biblioteca aceite.
 *
 * O `format` do `addImage` não é decorativo — passar "JPEG" para um PNG sai
 * corrompido no PDF. As fotos novas já nascem JPEG (a captura e a galeria
 * passam por canvas), mas as antigas do banco podem ser PNG, e o WebP o
 * jsPDF simplesmente não suporta: esse é reconvertido.
 */
function prepararParaPdf(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (/^data:image\/png/i.test(dataUrl)) {
        resolve({ dataUrl, w, h, formato: "PNG" });
        return;
      }
      if (/^data:image\/jpe?g/i.test(dataUrl)) {
        resolve({ dataUrl, w, h, formato: "JPEG" });
        return;
      }
      // WebP e afins: repassa por canvas para virar JPEG.
      try {
        const tela = document.createElement("canvas");
        tela.width = w;
        tela.height = h;
        tela.getContext("2d").drawImage(img, 0, 0);
        resolve({
          dataUrl: tela.toDataURL("image/jpeg", 0.92), w, h, formato: "JPEG",
        });
      } catch {
        resolve({ dataUrl, w, h, formato: "JPEG" });
      }
    };
    // Falha ao carregar não pode travar a impressão: 4:3 é o palpite menos ruim.
    img.onerror = () => resolve({ dataUrl, w: 1280, h: 960, formato: "JPEG" });
    img.src = dataUrl;
  });
}

export default function ComprovantesLoteModal({ loteId, numeroLote, onClose }) {
  const { request } = useAuth();
  const toast = useToast();

  const [erro, setErro] = useState(null);
  // [{id, numero, tipo, familia, detalhe|null, jpg:{dataUrl,blob,w,h}|null, erroGeracao}]
  const [itens, setItens] = useState(null);
  const [selecionados, setSelecionados] = useState(() => new Set());
  const [layoutValor, setLayoutValor] = useState(2);
  const cardRefs = useRef({});
  // `true` até o modal DESMONTAR de verdade. O loop de captura checa este
  // ref (não um `ativo` por-render) — assim os re-renders disparados pelos
  // próprios setItens NÃO abortam a captura em andamento (bug do 2º card
  // ficar "Gerando…" para sempre).
  const montadoRef = useRef(true);
  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);
  // Garante que o loop de captura roda UMA vez só.
  const capturaIniciada = useRef(false);

  // 1) Detalhe do lote → solicitações distintas → detalhe de cada uma.
  //    Detalhe que falhar (404/permissão/rede) marca erroGeracao NA HORA —
  //    senão o item nunca "processa" e os botões ficariam travados p/ sempre.
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const lote = await obterLote(request, loteId);
        const sols = lote?.solicitacoes || [];
        if (!ativo) return;
        if (!sols.length) {
          setItens([]);
          return;
        }
        setItens(sols.map((s) => ({ ...s, detalhe: null, jpg: null })));
        // Só os comprovantes vêm marcados. As imagens entram desmarcadas de
        // propósito: marcá-las por padrão dobraria/triplicaria a papelada de
        // quem só quer os comprovantes, que é o uso de todo dia deste modal.
        setSelecionados(new Set(sols.map((s) => `c-${s.id}`)));
        const detalhes = await Promise.all(
          sols.map((s) =>
            obterAssistencia(request, s.id).catch(() => null),
          ),
        );
        if (!ativo) return;
        setItens((prev) =>
          (prev || []).map((item, i) => ({
            ...item,
            detalhe: detalhes[i],
            erroGeracao: item.erroGeracao || !detalhes[i],
          })),
        );
      } catch (e) {
        if (ativo) setErro(e?.message || "Erro ao carregar o lote.");
      }
    })();
    return () => {
      ativo = false;
    };
  }, [request, loteId]);

  // 2) Captura os cards em SÉRIE, UMA única vez, quando todos os detalhes já
  //    resolveram (carregaram ou falharam). Percorre a lista congelada e
  //    comita cada JPG via setItens funcional; só para se o modal desmontar.
  useEffect(() => {
    if (capturaIniciada.current || !itens || itens.length === 0) return;
    const detalhesResolvidos = itens.every((i) => i.detalhe || i.erroGeracao);
    if (!detalhesResolvidos) return; // espera os detalhes de TODOS os itens
    capturaIniciada.current = true;
    const fila = itens; // snapshot estável para iterar
    (async () => {
      for (const base of fila) {
        if (!montadoRef.current) return;
        if (base.erroGeracao || !base.detalhe) continue; // já falhou / sem detalhe
        let el = cardRefs.current[base.id];
        for (let tent = 0; !el && tent < 30; tent += 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 50));
          if (!montadoRef.current) return;
          el = cardRefs.current[base.id];
        }
        try {
          if (!el) throw new Error("card não montou");
          // eslint-disable-next-line no-await-in-loop
          const blob = await jpgDeElemento(el);
          // eslint-disable-next-line no-await-in-loop
          const dataUrl = await new Promise((resolve, reject) => {
            const leitor = new FileReader();
            leitor.onload = () => resolve(leitor.result);
            leitor.onerror = () =>
              reject(new Error("Falha ao ler a imagem gerada."));
            leitor.readAsDataURL(blob);
          });
          // eslint-disable-next-line no-await-in-loop
          const dim = await new Promise((resolve) => {
            const img = new Image();
            img.onload = () =>
              resolve({ w: img.naturalWidth, h: img.naturalHeight });
            img.onerror = () => resolve({ w: 540, h: 960 });
            img.src = dataUrl;
          });
          if (!montadoRef.current) return;
          setItens((prev) =>
            (prev || []).map((i) =>
              i.id === base.id ? { ...i, jpg: { blob, dataUrl, ...dim } } : i,
            ),
          );
        } catch {
          if (!montadoRef.current) return;
          setItens((prev) =>
            (prev || []).map((i) =>
              i.id === base.id ? { ...i, erroGeracao: true } : i,
            ),
          );
        }
      }
    })();
  }, [itens]);

  const total = itens?.length || 0;

  /**
   * Lista ÚNICA do que pode ir para o papel: o comprovante de cada
   * solicitação seguido das imagens dela.
   *
   * A ordem já nasce agrupada — é ela que `montarPaginasAgrupadas` preserva
   * na hora de fatiar as folhas.
   */
  const pecas = useMemo(() => {
    const saida = [];
    (itens || []).forEach((item) => {
      saida.push({
        chave: `c-${item.id}`,
        tipo: "comprovante",
        solicitacaoId: item.id,
        numero: item.numero,
        item,
        pronto: Boolean(item.jpg),
        falhou: Boolean(item.erroGeracao),
      });
      paginasDaSolicitacao(item.detalhe).forEach((pagina, i) => {
        saida.push({
          chave: `f-${pagina.id}`,
          tipo: "imagem",
          solicitacaoId: item.id,
          numero: item.numero,
          ordem: i + 1,
          pagina,
          pronto: true, // a imagem já vem pronta no detalhe
          falhou: false,
        });
      });
    });
    return saida;
  }, [itens]);

  const prontas = useMemo(() => pecas.filter((p) => p.pronto), [pecas]);
  const escolhidas = useMemo(
    () => prontas.filter((p) => selecionados.has(p.chave)),
    [prontas, selecionados],
  );
  const imagensProntas = useMemo(
    () => prontas.filter((p) => p.tipo === "imagem"),
    [prontas],
  );
  const layout = LAYOUTS.find((l) => l.value === layoutValor) || LAYOUTS[0];
  // Processado = capturou OU falhou; travar botões só enquanto há item
  // realmente em processamento (falhas não seguram a tela).
  const processados = (itens || []).filter((i) => i.jpg || i.erroGeracao).length;
  const gerandoAinda = total > 0 && processados < total;

  const toggle = useCallback((chave) => {
    setSelecionados((prev) => {
      const prox = new Set(prev);
      if (prox.has(chave)) prox.delete(chave);
      else prox.add(chave);
      return prox;
    });
  }, []);

  const todasMarcadas =
    prontas.length > 0 && prontas.every((p) => selecionados.has(p.chave));

  /**
   * Marca/desmarca TUDO — inclusive o que ainda está sendo gerado.
   *
   * Mirar só no que já ficou pronto era um bug feio: as imagens ficam prontas
   * na hora, mas cada comprovante depende de uma captura em série. Quem
   * clicasse aqui nessa janela substituía a seleção pelos `f-*` e perdia os
   * `c-*` — e a impressão saía só com as fotos, sem nenhum comprovante, que é
   * justamente o que este modal existe para produzir. `escolhidas` já filtra
   * por `pronto`, então marcar o que ainda vem é inofensivo e correto.
   */
  const toggleTodas = () => {
    setSelecionados(
      todasMarcadas
        ? new Set()
        : new Set(pecas.filter((p) => !p.falhou).map((p) => p.chave)),
    );
  };

  const todasImagensMarcadas =
    imagensProntas.length > 0 &&
    imagensProntas.every((p) => selecionados.has(p.chave));
  const toggleImagens = () => {
    setSelecionados((prev) => {
      const prox = new Set(prev);
      imagensProntas.forEach((p) => {
        if (todasImagensMarcadas) prox.delete(p.chave);
        else prox.add(p.chave);
      });
      return prox;
    });
  };

  const exigirSelecao = () => {
    if (escolhidas.length === 0) {
      throw new Error("Selecione ao menos um comprovante ou imagem.");
    }
  };

  // ---- Baixar JPGs (um arquivo por peça) ----
  const baixarJpgs = async () => {
    exigirSelecao();
    escolhidas.forEach((peca, idx) => {
      let blob;
      let nome;
      if (peca.tipo === "comprovante") {
        blob = peca.item.jpg.blob;
        nome = `solicitacao-${peca.numero}.jpg`;
      } else {
        blob = dataUrlParaBlob(peca.pagina.imagem);
        // Extensão vem do tipo REAL: foto antiga em PNG salva como .jpg
        // abre torta em alguns visualizadores.
        const ext = (blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
        nome = `imagem-${peca.numero}-${peca.ordem}.${ext}`;
      }
      // Pequeno atraso entre downloads: alguns browsers descartam
      // downloads programáticos disparados no mesmo tick.
      setTimeout(() => baixarBlob(blob, nome), idx * 300);
    });
    toast.success(`${escolhidas.length} arquivo(s) sendo baixado(s).`);
  };

  // ---- Baixar PDF único (layout escolhido) ----
  const baixarPdf = async () => {
    exigirSelecao();
    const { celW, celH } = geometria(layout);
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    const paginas = montarPaginasAgrupadas(escolhidas, layout.value);

    // As imagens vêm do detalhe sem dimensão conhecida e em formato variado.
    const preparadas = new Map();
    await Promise.all(
      escolhidas
        .filter((p) => p.tipo === "imagem")
        .map(async (p) => {
          preparadas.set(p.chave, await prepararParaPdf(p.pagina.imagem));
        }),
    );

    // Desenha o cabeçalho como TEXTO do PDF (não rasterizado): sai vetorial,
    // legível em qualquer zoom, e não depende da resolução da foto.
    const desenharCabecalho = (peca, x, y) => {
      const { titulo, detalhe, lote } = peca.pagina.cabecalho || {};
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      const selo = [titulo, lote].filter(Boolean).join("   ");
      pdf.text(selo, x, y + 3);
      let usado = 4.5;
      if (detalhe) {
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7);
        const linha = pdf.splitTextToSize(detalhe, celW)[0] || "";
        pdf.text(linha, x, y + 6.6);
        usado = 8;
      }
      pdf.setDrawColor(200);
      pdf.line(x, y + usado, x + celW, y + usado);
      return usado + 1.5;
    };

    paginas.forEach((pagina, p) => {
      if (p > 0) pdf.addPage();
      pagina.forEach((peca, idx) => {
        const col = idx % layout.cols;
        const row = Math.floor(idx / layout.cols);
        const x0 = PAGINA.margem + col * (celW + PAGINA.gap);
        const y0 = PAGINA.margem + row * (celH + PAGINA.gap);

        const ehImagem = peca.tipo === "imagem";
        const fonte = ehImagem
          ? preparadas.get(peca.chave)
          : { ...peca.item.jpg, formato: "JPEG" };
        const alturaCabecalho = ehImagem ? desenharCabecalho(peca, x0, y0) : 0;
        const alturaArte = celH - alturaCabecalho;

        // Fit contain preservando proporção; centraliza na célula.
        const razao = fonte.w / fonte.h;
        let w = celW;
        let h = w / razao;
        if (h > alturaArte) {
          h = alturaArte;
          w = h * razao;
        }
        pdf.addImage(
          fonte.dataUrl, fonte.formato,
          x0 + (celW - w) / 2, y0 + alturaCabecalho, w, h,
        );
      });
    });
    pdf.save(`comprovantes-lote-${numeroLote || loteId}.pdf`);
  };

  // ---- Imprimir (janela dedicada, mesmo layout) ----
  const imprimir = async () => {
    exigirSelecao();
    const { celH } = geometria(layout);
    const paginas = montarPaginasAgrupadas(escolhidas, layout.value);

    const celulaHtml = (peca) => {
      if (peca.tipo === "comprovante") {
        return `
          <div class="cel">
            <img src="${esc(peca.item.jpg.dataUrl)}" alt="Comprovante ${esc(peca.numero)}" />
          </div>`;
      }
      const { titulo, detalhe, lote } = peca.pagina.cabecalho || {};
      return `
          <div class="cel cel-img">
            <div class="cab">
              <span class="num">${esc(titulo)}</span>
              <span class="det">${esc(detalhe)}</span>
              ${lote ? `<span class="lote">${esc(lote)}</span>` : ""}
            </div>
            <div class="arte">
              <img src="${esc(peca.pagina.imagem)}" alt="${esc(detalhe || titulo)}" />
            </div>
          </div>`;
    };

    const corpo = paginas
      .map(
        (pagina) => `
      <div class="pg">
        ${pagina.map(celulaHtml).join("")}
      </div>`,
      )
      .join("");
    const html = `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8" />
<title>Comprovantes do lote ${numeroLote || ""}</title>
<style>
  * { box-sizing: border-box; margin: 0; }
  @page { size: A4 portrait; margin: ${PAGINA.margem}mm; }
  .pg {
    display: grid;
    grid-template-columns: repeat(${layout.cols}, 1fr);
    grid-auto-rows: ${celH}mm;
    gap: ${PAGINA.gap}mm;
    page-break-after: always;
  }
  /* last-of-type (nao last-child): o <script> e o ultimo FILHO do body,
     entao last-child nunca casaria e toda impressao sairia com folha em
     branco extra nos browsers que honram o break final. */
  .pg:last-of-type { page-break-after: auto; }
  .cel {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    overflow: hidden;
  }
  .cel img { max-width: 100%; max-height: 100%; }

  /* Célula de imagem: cabeçalho fixo em cima, foto ocupando o resto.
     O cabeçalho é HTML (não desenhado na foto) para sair nítido na
     impressora — a foto é comprimida a 1280px, o texto não. */
  .cel-img {
    flex-direction: column;
    align-items: stretch;
    font-family: -apple-system, "Segoe UI", Roboto, sans-serif;
    color: #16181e;
  }
  .cel-img .cab {
    flex: none;
    display: flex;
    align-items: baseline;
    gap: 8px;
    border-bottom: 1px solid #c9ccd6;
    padding-bottom: 4px;
    margin-bottom: 5px;
  }
  .cel-img .cab .num { font-size: 11px; font-weight: 800; white-space: nowrap; }
  .cel-img .cab .det {
    font-size: 9px; color: #4b5060; flex: 1;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .cel-img .cab .lote {
    font-size: 9px; font-weight: 700; white-space: nowrap;
    border: 1px solid #c9ccd6; border-radius: 5px; padding: 0 5px;
  }
  .cel-img .arte {
    flex: 1; min-height: 0;
    display: flex; align-items: center; justify-content: center;
  }
  .cel-img .arte img { object-fit: contain; }
</style></head>
<body>${corpo}
<script>window.onload=function(){setTimeout(function(){window.print();},200);};</script>
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) {
      throw new Error("O navegador bloqueou a janela de impressão.");
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <SlideOverPanel
      open
      onClose={onClose}
      title={`Comprovantes do lote ${numeroLote || ""}`}
      subtitle="Baixe em JPG/PDF ou imprima — escolha quantos por folha"
      width="min(96vw, 720px)"
      footer={
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Label htmlFor="layout-impressao" style={{ margin: 0 }}>
              Posicionamento na folha
            </Label>
            <Select
              id="layout-impressao"
              value={layoutValor}
              onChange={(e) => setLayoutValor(Number(e.target.value))}
              style={{ width: "auto", minWidth: 200 }}
            >
              {LAYOUTS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </Select>
          </div>
          <RowEnd>
            <GhostBtn type="button" onClick={onClose}>Fechar</GhostBtn>
            <AsyncButton
              variant="secondary"
              silentSuccess
              disabled={gerandoAinda || escolhidas.length === 0}
              onClick={baixarJpgs}
            >
              <FiDownload size={14} /> JPGs ({escolhidas.length})
            </AsyncButton>
            <AsyncButton
              variant="secondary"
              silentSuccess
              disabled={gerandoAinda || escolhidas.length === 0}
              onClick={baixarPdf}
            >
              <FiFileText size={14} /> PDF ({escolhidas.length})
            </AsyncButton>
            <AsyncButton
              variant="primary"
              silentSuccess
              disabled={gerandoAinda || escolhidas.length === 0}
              onClick={imprimir}
            >
              <FiPrinter size={14} /> Imprimir ({escolhidas.length})
            </AsyncButton>
          </RowEnd>
        </div>
      }
    >
      {erro && <ErrorMsg>{erro}</ErrorMsg>}

      {!erro && itens === null && <Carregando />}

      {itens !== null && itens.length === 0 && (
        <Vazio titulo="Este lote não tem solicitações vinculadas" />
      )}

      {total > 0 && (
        <>
          {gerandoAinda && (
            <EmptyMsg>
              Gerando comprovantes… {Math.min(processados + 1, total)}/{total}
            </EmptyMsg>
          )}

          {prontas.length > 0 && (
            <ListaHeadRow>
              <Checkbox
                checked={todasMarcadas}
                onChange={toggleTodas}
                aria-label="Marcar ou desmarcar tudo"
              />
              <span>
                {escolhidas.length} de {prontas.length} selecionado(s)
                {gerandoAinda && ` · ${total - processados} comprovante(s) gerando`}
              </span>
              {imagensProntas.length > 0 && (
                <GhostBtn
                  type="button"
                  onClick={toggleImagens}
                  style={{ marginLeft: "auto", height: 30 }}
                >
                  {todasImagensMarcadas ? "Tirar" : "Incluir"} as{" "}
                  {imagensProntas.length} imagem(ns)
                </GhostBtn>
              )}
            </ListaHeadRow>
          )}

          <CompLoteGrid>
            {pecas.map((p) => {
              const ehImagem = p.tipo === "imagem";
              const rotulo = ehImagem
                ? `Imagem ${p.ordem} de ${p.numero}`
                : `Comprovante ${p.numero}`;
              const fonte = ehImagem ? p.pagina.imagem : p.item.jpg?.dataUrl;
              return (
                <CompLoteThumb
                  key={p.chave}
                  type="button"
                  $on={selecionados.has(p.chave)}
                  onClick={() => p.pronto && toggle(p.chave)}
                  disabled={!p.pronto}
                  title={
                    p.falhou ? "Falha ao gerar este comprovante" : rotulo
                  }
                >
                  {p.pronto ? (
                    <img src={fonte} alt={rotulo} />
                  ) : (
                    <div className="ph">{p.falhou ? "Falhou" : "Gerando…"}</div>
                  )}
                  <span className="num">
                    <Checkbox
                      checked={selecionados.has(p.chave)}
                      onChange={() => toggle(p.chave)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={!p.pronto}
                      aria-label={`Selecionar ${rotulo}`}
                    />
                    {ehImagem ? `${p.numero} · foto ${p.ordem}` : p.numero}
                  </span>
                </CompLoteThumb>
              );
            })}
          </CompLoteGrid>
        </>
      )}

      {/* Cards renderizados fora da tela, só para captura. Mantemos TODOS os
          cards com detalhe montados durante a vida do modal (não removemos ao
          ganhar o jpg) para os refs ficarem estáveis e a captura em série não
          perder o card do próximo item. */}
      <CompOffscreen aria-hidden="true">
        {(itens || [])
          .filter((i) => i.detalhe)
          .map((i) => (
            <ComprovanteCardView
              key={i.id}
              solicitacao={i.detalhe}
              familia={i.familia || "assistencia"}
              loteNumero={numeroLote}
              cardRef={(el) => {
                cardRefs.current[i.id] = el;
              }}
            />
          ))}
      </CompOffscreen>
    </SlideOverPanel>
  );
}
